import {flow} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {DateTime} from 'luxon';
import {Lens} from 'monocle-ts';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {ApplicationEnum} from 'Users/models/Application';
import {EmptyString, NonEmptyString, StringUnion} from './EmptyString';

export class InvalidAlert {
  public static fromAlert(alert: Alert) {
    return new InvalidAlert(
      alert.id,
      alert.title,
      alert.description,
      alert.startDate,
      alert.endDate,
      alert.applications
    );
  }

  public readonly key = 'InvalidAlert';

  public constructor(
    public readonly id: O.Option<string>,
    public readonly title: StringUnion,
    public readonly description: StringUnion,
    public readonly startDate: O.Option<DateTime>,
    public readonly endDate: O.Option<DateTime>,
    public readonly applications: ApplicationEnum[]
  ) {}
}

export class ValidAlert {
  public static create(alert: InvalidAlert) {
    const validatedTitle = NonEmptyString.create(alert.title.value);
    const validatedDescription = NonEmptyString.create(alert.description.value);

    return validatedTitle instanceof NonEmptyString &&
      validatedDescription instanceof NonEmptyString &&
      O.isSome(alert.startDate) &&
      O.isSome(alert.endDate)
      ? new ValidAlert(
          alert.id,
          validatedTitle,
          validatedDescription,
          alert.startDate,
          alert.endDate,
          alert.applications
        )
      : new InvalidAlert(alert.id, alert.title, alert.description, alert.startDate, alert.endDate, alert.applications);
  }

  public readonly key = 'ValidAlert';

  private constructor(
    public readonly id: O.Option<string>,
    public readonly title: NonEmptyString,
    public readonly description: NonEmptyString,
    public readonly startDate: O.Some<DateTime>,
    public readonly endDate: O.Some<DateTime>,
    public readonly applications: ApplicationEnum[]
  ) {}
}

export class AlertWithErrors {
  public static fromAlert(alert: Alert, errors: string) {
    return new AlertWithErrors(
      alert.id,
      alert.title,
      alert.description,
      alert.startDate,
      alert.endDate,
      alert.applications,
      errors
    );
  }

  public readonly key = 'AlertWithErrors';

  constructor(
    public readonly id: O.Option<string>,
    public readonly title: StringUnion,
    public readonly description: StringUnion,
    public readonly startDate: O.Option<DateTime>,
    public readonly endDate: O.Option<DateTime>,
    public readonly applications: ApplicationEnum[],
    public readonly errors: string
  ) {}
}

const alertLens = Lens.fromProp<Alert>();
const withAlertValidation = flow(InvalidAlert.fromAlert, ValidAlert.create);

export const changeTitle = (title: string) => (existingAlert: Alert) =>
  pipe(alertLens('title').set(NonEmptyString.create(title))(existingAlert), withAlertValidation);
export const changeDescription = (description: string) => (existingAlert: Alert) =>
  pipe(alertLens('description').set(NonEmptyString.create(description))(existingAlert), withAlertValidation);
export const changeStartDate = (startDate: DateTime | null) => (existingAlert: Alert) =>
  pipe(alertLens('startDate').set(O.fromNullable(startDate))(existingAlert), withAlertValidation);
export const changeEndDate = (endDate: DateTime | null) => (existingAlert: Alert) =>
  pipe(alertLens('endDate').set(O.fromNullable(endDate))(existingAlert), withAlertValidation);
export const changeApplications = (applications: ApplicationEnum[]) => (existingAlert: Alert) =>
  pipe(alertLens('applications').set(applications)(existingAlert), withAlertValidation);

export type Alert = ValidAlert | InvalidAlert | AlertWithErrors;

export type AlertOptions<TReturn> = ToMatchOptions<Alert, 'key', TReturn>;

export const matchAlert = <TReturn>(alert: Alert, options: AlertOptions<TReturn>): TReturn => {
  switch (alert.key) {
    case 'InvalidAlert':
      return options.InvalidAlert(alert);

    case 'AlertWithErrors':
      return options.AlertWithErrors(alert);

    case 'ValidAlert':
      return options.ValidAlert(alert);
  }
};

export const EmptyAlert = new InvalidAlert(O.none, new EmptyString(), new EmptyString(), O.none, O.none, []);
